import React, { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Toaster } from "react-hot-toast";

import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import {
  PageHeader,
  SectionCard,
  ThemedDataGrid,
  DynamicDataViewer,
} from "_shared/ui";
import { getSeAnalyticsDailyCalls, getSeAnalyticsFilters } from "_controllers/officer";

const fmtDate = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Main = () => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [callType, setCallType] = useState("");
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [filterOptions, setFilterOptions] = useState({ callTypes: [] });

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return fmtDate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => fmtDate(new Date()));

  const [selectedRow, setSelectedRow] = useState(null);

  const [zone, setZone] = useState("");
  const [region, setRegion] = useState("");
  const [sapId, setSapId] = useState("");



  useEffect(() => {
    (async () => {
      try {
        const data = await getSeAnalyticsFilters();
        if (data) setFilterOptions(data);
      } catch (err) {
        console.log("filters error", err);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchApplied(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getSeAnalyticsDailyCalls({
          zone,
          region,
          sapId,
          start: startDate,
          end: endDate,
          type: callType,
          search: searchApplied,
          page: page + 1,
          limit: pageSize,
        });
        setRows(res?.data || []);
        setTotalRows(res?.totalData || 0);
      } catch (err) {
        console.log("call details error", err);
        setRows([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [zone, region, sapId, callType, searchApplied, page, pageSize, startDate, endDate]);

  const callDetailColumns = useMemo(
    () => [
      { field: "employeeName", headerName: "SE", width: 160 },
      { field: "SAP_ID", headerName: "Emp Code", width: 110 },
      { field: "zone", headerName: "Zone", width: 80 },
      { field: "regionName", headerName: "Region", width: 130 },
      { field: "callType", headerName: "Call Type", width: 180 },
      { field: "date", headerName: "Date & Time", width: 150 },
      { field: "timeSpent", headerName: "Time Spent", width: 120 },
      { field: "info", headerName: "Details", width: 300, flex: 1 },
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        renderCell: (params) => (
          <Button 
            size="small" 
            variant="outlined"
            onClick={() => setSelectedRow(params.row)}
          >
            View Data
          </Button>
        )
      }
    ],
    []
  );

  return (
    <>
      <PageHeader
        title="Daily Calls Data"
        subtitle="SE Daily Calls Details (Click View Data to see raw DB document)"
      />
      <SectionCard
        title="Daily Call Data Details"
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Zone</InputLabel>
              <Select value={zone} label="Zone" onChange={(e) => { setZone(e.target.value); setPage(0); }}>
                <MenuItem value="">All Zones</MenuItem>
                {filterOptions.zones?.map((z, i) => {
                  const val = typeof z === 'object' ? z.zoneCode || z.zoneName : z;
                  const label = typeof z === 'object' ? z.zoneName || z.zoneCode : z;
                  return <MenuItem key={val || i} value={val || ""}>{label}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Region</InputLabel>
              <Select value={region} label="Region" onChange={(e) => { setRegion(e.target.value); setPage(0); }}>
                <MenuItem value="">All Regions</MenuItem>
                {filterOptions.regions?.map((r, i) => {
                  const val = typeof r === 'object' ? r.regionCode || r.regionName : r;
                  const label = typeof r === 'object' ? r.regionName || r.regionCode : r;
                  return <MenuItem key={val || i} value={val || ""}>{label}</MenuItem>;
                })}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Call Type</InputLabel>
              <Select
                label="Call Type"
                value={callType}
                onChange={(e) => {
                  setCallType(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {(filterOptions.callTypes || []).map((ct) => (
                  <MenuItem key={ct} value={ct}>
                    {ct}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search SE / type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Box>
        }
      >
        <ThemedDataGrid
          rows={rows}
          columns={callDetailColumns}
          getRowId={(row) => row._id || row.id || Math.random().toString()}
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          page={page}
          onPageChange={(p) => setPage(p)}
          pageSize={pageSize}
          onPageSizeChange={(s) => setPageSize(s)}
          rowsPerPageOptions={[25, 50, 100]}
          height={650}
        />
      </SectionCard>

      <Dialog 
        open={Boolean(selectedRow)} 
        onClose={() => setSelectedRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Raw Daily Call Document</Typography>
          <IconButton onClick={() => setSelectedRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 1, overflowX: "hidden" }}>
             <DynamicDataViewer data={selectedRow} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRow(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DailyCallsDataPage = () => {
  return (
    <AccessController roles={["ADMIN", "ADMINSE"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default DailyCallsDataPage;
